import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NAV } from "@/data/site";
import { openWhatsApp } from "@/lib/wa";
import { BUSINESS } from "@/data/site";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const bookMsg = `Merhaba ${BUSINESS.name}, randevu oluşturmak istiyorum.`;

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
          scrolled
            ? "bg-ivory/90 backdrop-blur-xl shadow-[0_1px_0_rgba(36,27,24,0.08)] py-3"
            : "bg-transparent py-5"
        }`}
      >
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 flex items-center justify-between">
          <a href="#hero" aria-label="Ana Sayfa" data-testid="header-logo-link">
            <Logo className={`w-auto transition-all duration-500 ${scrolled ? "h-7 sm:h-8" : "h-9 sm:h-11"}`} testid="header-logo" />
          </a>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`nav-${item.href.replace("#", "")}`}
                className="link-underline text-espresso/85 hover:text-espresso text-sm font-medium tracking-wide"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => openWhatsApp(bookMsg)}
              data-testid="header-book-button"
              className="hidden sm:inline-flex items-center rounded-full bg-espresso px-6 py-2.5 text-ivory text-sm font-medium tracking-wide transition-transform duration-300 hover:scale-[1.03] active:scale-95"
            >
              Randevu Al
            </button>
            <button
              onClick={() => setOpen(true)}
              aria-label="Menüyü aç"
              data-testid="mobile-menu-open"
              className="lg:hidden text-espresso p-1"
            >
              <Menu strokeWidth={1.4} size={28} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] bg-espresso text-ivory grain overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="mobile-menu-overlay"
          >
            <div className="relative z-10 flex h-full flex-col px-6 py-6">
              <div className="flex items-center justify-between">
                <span className="font-serif text-2xl text-gold">{BUSINESS.name}</span>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Menüyü kapat"
                  data-testid="mobile-menu-close"
                  className="text-ivory p-1"
                >
                  <X strokeWidth={1.4} size={30} />
                </button>
              </div>

              <nav className="mt-16 flex flex-col gap-1">
                {NAV.map((item, i) => (
                  <motion.a
                    key={item.href}
                    href={item.href}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${item.href.replace("#", "")}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + i * 0.06, duration: 0.5 }}
                    className="font-serif text-4xl sm:text-5xl py-2 border-b border-ivory/10 text-ivory/90"
                  >
                    {item.label}
                  </motion.a>
                ))}
              </nav>

              <div className="mt-auto">
                <button
                  onClick={() => {
                    setOpen(false);
                    openWhatsApp(bookMsg);
                  }}
                  data-testid="mobile-menu-book-button"
                  className="w-full rounded-full bg-gold py-4 text-espresso text-base font-semibold tracking-wide"
                >
                  WhatsApp’tan Randevu Al
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
